import React, { useState, useMemo, useRef } from 'react'
import { ChevronDown, Search, Funnel, FunnelX } from 'lucide-react'
import {
  Icon,
  iconCatalog,
  ICON_SIZES,
  ICON_STYLE_IDS,
  type IconName,
  type IconSize,
  type IconStyleId,
} from '@restaurant/core'
import type { ShowcaseSettings } from '../../../pages/ComponentShowcase'
import { ICON_STYLE_LABELS } from '../../../pages/ComponentShowcase'

/**
 * Icon gallery.
 *
 * One card per catalog name, previewed on the same page-background token the
 * component preview canvas uses and drawn with a bare `<Icon name size />` —
 * no className, no color — so each glyph picks up `--theme-icon` and the active
 * style exactly as a shipped component would. Multi-size names show every cut
 * side by side at its real token size, which is the whole point of the view.
 *
 * The dark chrome, hover-open filter dropdown and centered expanding search are
 * deliberately copied from LandingComponentsSection rather than shared with it:
 * that file is a review tool with its own status/comment machinery, and folding
 * a second consumer into it would couple two screens that are free to diverge.
 *
 * Icon style is NOT stored here. It lives in `ShowcaseSettings.iconStyle` and is
 * pushed into the core icon style store by ComponentShowcase; the pill row below
 * writes through the same settings updater the rest of the showcase uses.
 */

interface IconsSectionProps {
  settings: ShowcaseSettings
  onSettingsChange: (settings: ShowcaseSettings) => void
}

// Text highlighting for search matches
function HighlightedText({
  text,
  highlight,
  className = ''
}: {
  text: string
  highlight: string
  className?: string
}) {
  if (!highlight.trim()) {
    return <span className={className}>{text}</span>
  }

  const regex = new RegExp(`(${highlight.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi')
  const parts = text.split(regex)

  return (
    <span className={className}>
      {parts.map((part, index) =>
        regex.test(part) ? (
          <span key={index} style={{ backgroundColor: '#fe9a00' }}>{part}</span>
        ) : (
          <span key={index}>{part}</span>
        )
      )}
    </span>
  )
}

// Filter option type
interface FilterOption {
  value: string
  label: string
}

// Filter group for the combined dropdown
interface FilterGroup {
  label: string
  options: FilterOption[]
  selectedValues: Set<string>
  onToggle: (value: string, shiftKey: boolean) => void
}

interface CombinedFilterDropdownProps {
  groups: FilterGroup[]
  hasActiveFilters: boolean
  onClearFilters: () => void
}

function CombinedFilterDropdown({ groups, hasActiveFilters, onClearFilters }: CombinedFilterDropdownProps) {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
      <div
        ref={dropdownRef}
        style={{ position: 'relative' }}
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
      >
        <button
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            backgroundColor: isOpen ? '#171717' : '#0a0a0a',
            border: 'none',
            cursor: 'pointer',
            fontFamily: "'Figtree', sans-serif",
            fontSize: '14px',
            fontWeight: 630,
            color: '#fafafa',
            padding: '4px 8px',
            borderRadius: isOpen ? '6px 6px 0 0' : '6px',
          }}
        >
          <Funnel style={{ width: 14, height: 14 }} />
          <span>Filter</span>
          <ChevronDown className="h-3 w-3" />
        </button>

        {isOpen && (
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              backgroundColor: '#171717',
              borderRadius: '0 6px 6px 6px',
              padding: '6px',
              zIndex: 100,
              minWidth: '380px',
            }}
          >
            {groups.map((group, groupIndex) => (
              <div key={group.label}>
                <div
                  style={{
                    fontFamily: "'Figtree', sans-serif",
                    fontSize: '10px',
                    fontWeight: 600,
                    color: '#737373',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    padding: groupIndex === 0 ? '4px 4px 6px 4px' : '12px 4px 6px 4px',
                  }}
                >
                  {group.label}
                </div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(${Math.min(group.options.length, 5)}, 1fr)`,
                    gap: '4px',
                  }}
                >
                  {group.options.map((option) => {
                    const isSelected = group.selectedValues.has(option.value)
                    return (
                      <button
                        key={option.value}
                        onClick={(e) => group.onToggle(option.value, e.shiftKey)}
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '4px',
                          fontFamily: "'Figtree', sans-serif",
                          fontSize: '12px',
                          fontWeight: isSelected ? 630 : 500,
                          color: isSelected ? '#ffffff' : '#a1a1a1',
                          backgroundColor: isSelected ? '#404040' : '#262626',
                          border: isSelected ? '1px solid #737373' : '1px solid transparent',
                          borderRadius: '4px',
                          padding: '8px 0',
                          cursor: 'pointer',
                          minWidth: '68px',
                        }}
                      >
                        {option.label}
                      </button>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {hasActiveFilters && (
        <button
          onClick={onClearFilters}
          title="Clear all filters"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: '4px 8px',
            borderRadius: '6px',
          }}
        >
          <FunnelX style={{ width: 14, height: 14, color: '#fb2c36' }} />
        </button>
      )}
    </div>
  )
}

/**
 * `Icon`'s `size` is narrowed per literal name by `IconSizeFor<N>`, which TS
 * cannot resolve when the name comes out of a runtime iteration. Every pair we
 * render is read straight off `iconCatalog`, so the pair is valid by
 * construction — this is the single place that fact is re-asserted.
 */
const CatalogIcon = Icon as unknown as (props: { name: IconName; size: IconSize }) => React.ReactElement

// One catalog entry, narrowed to the cuts the size filter lets through
interface IconEntry {
  name: IconName
  /** Every size this name is authored at. */
  allSizes: IconSize[]
  /** The subset currently being previewed. */
  visibleSizes: IconSize[]
}

function IconCard({ entry, searchTerm }: { entry: IconEntry; searchTerm: string }) {
  const [isHovered, setIsHovered] = useState(false)

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        borderRadius: '9px',
        border: `1px solid ${isHovered ? '#737373' : '#404040'}`,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#262626',
        transition: 'border-color 0.15s ease',
      }}
    >
      {/* Preview surface — the same token the component preview canvas paints
          with, so the glyph is read against the background it ships on. */}
      <div
        style={{
          minHeight: '84px',
          backgroundColor: 'var(--theme-fill-page-background)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '16px',
          padding: '12px',
        }}
      >
        {entry.visibleSizes.map((size) => (
          <CatalogIcon key={size} name={entry.name} size={size} />
        ))}
      </div>

      {/* Name + the sizes this glyph is authored at */}
      <div
        style={{
          minHeight: '34px',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px',
          padding: '6px 10px',
        }}
      >
        <span
          style={{
            fontFamily: "'Menlo', monospace",
            fontSize: '12px',
            color: '#fafafa',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
          }}
        >
          <HighlightedText text={entry.name} highlight={searchTerm} />
        </span>
        <span style={{ display: 'flex', gap: '3px', flexShrink: 0 }}>
          {entry.allSizes.map((size) => {
            const isVisible = entry.visibleSizes.includes(size)
            return (
              <span
                key={size}
                title={`Authored at ${size}`}
                style={{
                  fontFamily: "'Menlo', monospace",
                  fontSize: '9px',
                  lineHeight: 1,
                  padding: '3px 4px',
                  borderRadius: '3px',
                  backgroundColor: isVisible ? '#525252' : 'transparent',
                  border: `1px solid ${isVisible ? '#737373' : '#404040'}`,
                  color: isVisible ? '#fafafa' : '#737373',
                }}
              >
                {size}
              </span>
            )
          })}
        </span>
      </div>
    </div>
  )
}

// Every catalog name with its authored sizes, sorted alphabetically. The catalog
// is a frozen generated constant, so this is computed once at module load.
const CATALOG_ENTRIES: { name: IconName; allSizes: IconSize[] }[] = (
  Object.entries(iconCatalog) as [IconName, readonly IconSize[]][]
)
  .map(([name, sizes]) => ({
    name,
    // Keep the manifest's xs -> xl ordering rather than whatever order the
    // catalog literal happened to list.
    allSizes: ICON_SIZES.filter((s: IconSize) => sizes.includes(s)),
  }))
  .sort((a, b) => a.name.localeCompare(b.name))

export default function IconsSection({ settings, onSettingsChange }: IconsSectionProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [isSearchFocused, setIsSearchFocused] = useState(false)
  const [sizeFilters, setSizeFilters] = useState<Set<IconSize>>(() => new Set(ICON_SIZES))
  const searchInputRef = useRef<HTMLInputElement>(null)

  const setIconStyleSetting = (styleId: IconStyleId) => {
    onSettingsChange({ ...settings, iconStyle: styleId })
  }

  const hasActiveFilters = sizeFilters.size < ICON_SIZES.length

  const clearFilters = () => setSizeFilters(new Set(ICON_SIZES))

  const toggleSize = (value: string, shiftKey: boolean) => {
    const size = value as IconSize
    setSizeFilters((prev) => {
      if (shiftKey) {
        const next = new Set(prev)
        if (next.has(size)) {
          next.delete(size)
          if (next.size === 0) return new Set(ICON_SIZES)
        } else {
          next.add(size)
        }
        return next
      }
      if (prev.size === 1 && prev.has(size)) return new Set(ICON_SIZES)
      return new Set([size])
    })
  }

  // Filtering keeps a name whenever any of its cuts survives the size filter,
  // and previews only the surviving cuts.
  const entries = useMemo<IconEntry[]>(() => {
    const lowerSearch = searchTerm.toLowerCase().trim()

    return CATALOG_ENTRIES.reduce<IconEntry[]>((acc, entry) => {
      if (lowerSearch && !entry.name.toLowerCase().includes(lowerSearch)) return acc

      const visibleSizes = entry.allSizes.filter((size) => sizeFilters.has(size))
      if (visibleSizes.length === 0) return acc

      acc.push({ name: entry.name, allSizes: entry.allSizes, visibleSizes })
      return acc
    }, [])
  }, [searchTerm, sizeFilters])

  const totalCuts = useMemo(
    () => entries.reduce((sum, entry) => sum + entry.visibleSizes.length, 0),
    [entries]
  )

  return (
    <div className="flex flex-col h-full" style={{ backgroundColor: '#0a0a0a' }}>
      {/* Search/Filter Bar */}
      <div
        style={{
          height: '60px',
          backgroundColor: '#000000',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 24px',
          position: 'relative',
          borderBottom: '1px solid #262626',
        }}
      >
        {/* Filters - Left */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            flex: '0 0 auto',
            opacity: isSearchFocused ? 0 : 1,
            pointerEvents: isSearchFocused ? 'none' : 'auto',
            transition: 'opacity 200ms ease',
            zIndex: 1,
          }}
        >
          <CombinedFilterDropdown
            hasActiveFilters={hasActiveFilters}
            onClearFilters={clearFilters}
            groups={[
              {
                label: 'Size',
                options: ICON_SIZES.map((size: IconSize) => ({ value: size, label: size })),
                selectedValues: sizeFilters as Set<string>,
                onToggle: toggleSize,
              },
            ]}
          />
        </div>

        {/* Search Bar - Center (absolutely positioned so it stays centered) */}
        <div
          style={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            width: isSearchFocused ? '450px' : '300px',
            transition: 'width 200ms ease',
            margin: '-1px',
            padding: '1px',
          }}
        >
          <div
            style={{
              position: 'absolute',
              inset: 0,
              borderRadius: '10px',
              border: '2px solid #d4d4d4',
              opacity: isSearchFocused ? 1 : 0,
              transition: 'opacity 200ms ease',
              pointerEvents: 'none',
            }}
          />
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              backgroundColor: searchTerm ? '#525252' : '#262626',
              borderRadius: '8px',
              padding: '8px 12px',
              width: '100%',
            }}
          >
            <Search className="h-4 w-4" style={{ color: '#d4d4d4', flexShrink: 0 }} />
            <input
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={() => setIsSearchFocused(true)}
              onBlur={() => setIsSearchFocused(false)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setSearchTerm('')
                  searchInputRef.current?.blur()
                } else if (e.key === 'Backspace' && searchTerm === '') {
                  searchInputRef.current?.blur()
                }
              }}
              placeholder=""
              style={{
                flex: 1,
                background: 'none',
                border: 'none',
                outline: 'none',
                fontFamily: "'Menlo', monospace",
                fontSize: '12px',
                fontWeight: 400,
                color: '#fafafa',
              }}
            />
          </div>
        </div>

        {/* Count - Right */}
        <div
          style={{
            flex: '0 0 auto',
            opacity: isSearchFocused ? 0 : 1,
            pointerEvents: 'none',
            transition: 'opacity 200ms ease',
            zIndex: 1,
            fontFamily: "'Menlo', monospace",
            fontSize: '12px',
            color: '#737373',
          }}
        >
          {entries.length} names · {totalCuts} cuts
        </div>
      </div>

      {/* Style pill row — the single source of truth is the global showcase
          setting, so switching here is the same action as switching in the
          preview panel's Options tab. */}
      <div
        style={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '6px',
          padding: '10px 24px',
          backgroundColor: '#000000',
          borderBottom: '1px solid #262626',
        }}
      >
        <span
          style={{
            fontFamily: "'Figtree', sans-serif",
            fontSize: '10px',
            fontWeight: 600,
            color: '#737373',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            marginRight: '4px',
          }}
        >
          Style
        </span>
        {ICON_STYLE_IDS.map((styleId: IconStyleId) => {
          const isSelected = settings.iconStyle === styleId
          return (
            <button
              key={styleId}
              onClick={() => setIconStyleSetting(styleId)}
              title={styleId}
              style={{
                fontFamily: "'Figtree', sans-serif",
                fontSize: '12px',
                fontWeight: isSelected ? 630 : 500,
                color: isSelected ? '#ffffff' : '#a1a1a1',
                backgroundColor: isSelected ? '#404040' : '#262626',
                border: isSelected ? '1px solid #737373' : '1px solid transparent',
                borderRadius: '999px',
                padding: '4px 12px',
                cursor: 'pointer',
              }}
            >
              {ICON_STYLE_LABELS[styleId]}
            </button>
          )
        })}
      </div>

      {/* Card grid */}
      <div className="flex-1 overflow-auto p-8">
        {entries.length === 0 ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              gap: '16px',
            }}
          >
            <span
              style={{
                fontFamily: "'Figtree', sans-serif",
                fontSize: '14px',
                fontWeight: 630,
                color: '#d4d4d4',
              }}
            >
              No matching icons
            </span>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                style={{
                  fontFamily: "'Figtree', sans-serif",
                  fontSize: '12px',
                  fontWeight: 630,
                  color: '#fafafa',
                  backgroundColor: '#262626',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '8px 16px',
                  cursor: 'pointer',
                }}
              >
                Clear Filters
              </button>
            )}
          </div>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: '12px',
              opacity: isSearchFocused ? 0.4 : 1,
              transition: 'opacity 200ms ease',
            }}
          >
            {entries.map((entry) => (
              <IconCard key={entry.name} entry={entry} searchTerm={searchTerm} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
